import type { RunwayVideoModelDefinition } from './video-model-types'

/** Canonical Runway video catalog, including capabilities and quote rules. */
export const RUNWAY_VIDEO_MODEL_MANIFEST = {
  'gen4.5': {
    provider: 'runway',
    providerModel: 'gen4.5',
    modelName: 'Gen-4.5',
    description: 'High-fidelity motion and visual detail.',
    isPreview: false,
    requestOptions: { includeAudio: false, includeNegativePrompt: false },
    capabilities: {
      modes: ['text', 'frames'],
      durations: { min: 2, max: 10, step: 1 },
      aspectRatios: ['16:9', '9:16', '1:1'],
      aspectRatiosByMode: { text: ['16:9', '9:16'] },
      resolutions: ['720p'],
      supportsLastFrame: false,
      supportsAudio: false,
      audioMode: 'unsupported',
      supportsNegativePrompt: false,
      promptMaxLength: 1000,
      inputAspectRatio: { min: 0.5, max: 2 }
    },
    providerRatios: {
      '360p': {},
      '480p': {},
      '720p': { '16:9': '1280:720', '9:16': '720:1280', '1:1': '960:960' },
      '1080p': {},
      '4k': {}
    },
    pricing: { kind: 'per-second', creditsPerSecond: 12 }
  },
  'veo3.1_fast': {
    provider: 'runway',
    providerModel: 'veo3.1_fast',
    modelName: 'Veo 3.1 Fast',
    description: 'Faster video generation with optional audio.',
    isPreview: false,
    requestOptions: { includeAudio: true, includeNegativePrompt: true },
    capabilities: {
      modes: ['text', 'frames'],
      durations: { min: 4, max: 8, step: 2, values: [4, 6, 8] },
      aspectRatios: ['16:9', '9:16'],
      resolutions: ['720p', '1080p'],
      supportsLastFrame: true,
      supportsAudio: true,
      audioMode: 'optional',
      supportsNegativePrompt: true,
      promptMaxLength: 1000,
      inputAspectRatio: { min: 0.5, max: 2 }
    },
    providerRatios: {
      '360p': {},
      '480p': {},
      '720p': { '16:9': '1280:720', '9:16': '720:1280' },
      '1080p': { '16:9': '1920:1080', '9:16': '1080:1920' },
      '4k': {}
    },
    pricing: { kind: 'per-second-with-audio', creditsPerSecond: 10, creditsPerSecondWithAudio: 15 }
  },
  seedance2_5: {
    provider: 'runway',
    providerModel: 'seedance2_5',
    modelName: 'Seedance 2.5',
    description: 'Flexible references, video editing and extension.',
    isPreview: false,
    requestOptions: { includeAudio: true, includeNegativePrompt: false },
    capabilities: {
      modes: ['text', 'frames', 'references', 'edit', 'extend'],
      durations: { min: 4, max: 30, step: 1 },
      aspectRatios: ['16:9', '9:16', '1:1'],
      resolutions: ['480p', '720p', '1080p'],
      supportsLastFrame: true,
      supportsAudio: true,
      audioMode: 'optional',
      supportsNegativePrompt: false,
      promptMaxLength: 15000,
      inputAspectRatio: { min: 0.4, max: 4 },
      referenceLimits: { images: 30, videos: 10, audio: 10, combinedSeconds: 30 },
      referenceLimitsByMode: {
        frames: { images: 0, videos: 0, audio: 10, combinedSeconds: 30 },
        edit: { images: 30, videos: 9, audio: 10, combinedSeconds: 30 },
        extend: { images: 30, videos: 9, audio: 10, combinedSeconds: 30 }
      },
      maxInputVideos: 10
    },
    providerRatios: {
      '360p': {},
      '480p': { '16:9': '854:480', '9:16': '480:854', '1:1': '640:640' },
      '720p': { '16:9': '1280:720', '9:16': '720:1280', '1:1': '960:960' },
      '1080p': { '16:9': '1920:1080', '9:16': '1080:1920', '1:1': '1440:1440' },
      '4k': {}
    },
    pricing: {
      kind: 'resolution-based',
      minimumCredits: 80,
      creditsPerSecondByResolution: { '480p': 20, '720p': 30, '1080p': 68 }
    }
  }
} as const satisfies Readonly<Record<string, RunwayVideoModelDefinition>>
